/*
Hook 1: useVenues (cache + fetch)

Owns venues, initial cache hydration, refresh, loading.
	•	state: venues, loading
	•	uses CACHE_KEY, CACHE_DURATION_MS
	•	calls getVenues()

Return: { venues, loading, refresh }
*/


// src/hooks/useVenues.js
import { useCallback, useEffect, useRef, useState } from "react";
import { CACHE_KEY, CACHE_DURATION_MS } from "@/constants/cache";
import { getVenues } from "@/api/venuesApi";

function safeReadCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;

    const { data, timestamp } = parsed;
    if (!Array.isArray(data)) return null;
    if (typeof timestamp !== "number") return null;

    return parsed;
  } catch {
    return null;
  }
}

function writeCache(data) {
  try {
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ data, timestamp: Date.now() })
    );
  } catch {}
}

export function useVenues() {
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);

  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
  setLoading(true);

  try {
    const res = await getVenues();
    if (!mountedRef.current) return;

    setVenues(res);
    writeCache(res);
  } catch (e) {
    console.error("Failed to refresh venues:", e);
  } finally {

    if (!mountedRef.current) return;
    setLoading(false);

  }
}, []);

  // initial load
  useEffect(() => {
    const cached = safeReadCache();

    if (cached) {
      const age = Date.now() - cached.timestamp;
      setVenues(cached.data);
      setLoading(false);

      if (age > CACHE_DURATION_MS) {
        refresh(); // background refresh
      }
    } else {
      refresh();
    }
  }, [refresh]);

  return { venues, loading, refresh };
}